// ============================================
// Admin Join Event
// ============================================

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminJoin() {
  const { user, loading: authLoading, signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();

  const [code, setCode] = React.useState(searchParams.get('code') || '');
  const [eventId] = React.useState(searchParams.get('event') || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  // Debug logging
  React.useEffect(() => {
    const urlCode = searchParams.get('code');
    const urlEvent = searchParams.get('event');
    console.log('URL params:', { urlCode, urlEvent });
    console.log('AdminJoin state:', { authLoading, user: user?.email || 'none', code, eventId });
  }, [authLoading, user, code, eventId, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Please enter an invite code');
      return;
    }

    if (!user) {
      setError('Please sign in first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const redeemInviteCode = httpsCallable(functions, 'redeemInviteCode');
      const result: any = await redeemInviteCode({
        code: code.trim().toUpperCase(),
        eventId: eventId || undefined,
      });

      setSuccess(true);

      // Force token refresh to get new role claims BEFORE redirecting
      console.log('Forcing token refresh to get new admin role...');
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        console.log('Token refreshed successfully');
      }

      // Redirect to event after a short delay
      setTimeout(() => {
        window.location.href = `/admin/events/${result.data.eventId}`;
      }, 1500);
    } catch (err: any) {
      console.error('Error redeeming code:', err);
      if (err.message?.includes('not authorized')) {
        setError('Your email address is not authorized for this invite. Contact the organizer.');
      } else {
        setError(err.message || 'Invalid or expired invite code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('Sign in error:', err);
    }
  };

  // Show loading while auth state is being determined
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ marginBottom: '1rem' }}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="therapist-join success" style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="success-message" style={{ textAlign: 'center' }}>
          <span className="icon" style={{ fontSize: '3rem', color: 'var(--color-green)' }}>✓</span>
          <h2>Welcome, Admin!</h2>
          <p>You've joined the event successfully.</p>
          <p>Redirecting to event dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="therapist-join" style={{ minHeight: '100vh', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
      <div className="join-container" style={{ maxWidth: '400px', width: '100%', padding: '2rem' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem', color: '#fff' }}>Join as Event Admin</h1>
        <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '2rem' }}>
          Enter the invite code to become an admin for this event.
        </p>

        {!user ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '1rem', color: '#aaa' }}>
              Please sign in with your Google account first.
            </p>
            <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem', color: '#f59e0b' }}>
              Make sure to use the email address that was invited.
            </p>
            <button onClick={handleSignIn} className="btn btn-large btn-google" style={{ width: '100%', padding: '1rem', fontSize: '1rem', background: '#4285f4', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              Sign in with Google
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="join-form">
            {error && <div className="error-message">{error}</div>}

            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--color-bg-secondary)', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                Signed in as:
              </p>
              <p style={{ fontWeight: 600 }}>{user.email}</p>
            </div>

            <div className="form-group">
              <label htmlFor="code">Invite Code</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                className="code-input"
                maxLength={10}
                autoComplete="off"
                autoFocus
                style={{ fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.25rem' }}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-large" disabled={loading} style={{ width: '100%', marginTop: '1rem' }}>
              {loading ? 'Joining...' : 'Join as Admin'}
            </button>
          </form>
        )}

        <div className="join-help" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>Don't have a code?</p>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            Contact the event organizer to receive an invite.
          </p>
        </div>
      </div>
    </div>
  );
}
